import { inject, Injectable } from "@angular/core";

import { IPagedGridSettings, IQueryPageRequest } from "@main/reporting.v3/core/reporting-models";
import { IEmittedVariable, IQueryVariables } from "@main/reporting.v3/core/variables/variable-models";

import { QUERY_RESOLVER } from "../constants/query-hub.constants";
import { QueryData, QueryHub, QueryResolver } from "../models";


@Injectable()
export class QueryHubService implements QueryHub {

    private queries: Record<string, QueryData> = {};
    private identifiers: Record<string, string> = {};

    private resolver: QueryResolver = inject(QUERY_RESOLVER);

    public async requestQueryId(queryName: string): Promise<string> {
        if (!queryName?.length) return null;

        this.identifiers[queryName] ??= await this.resolver.getQueryId(queryName);

        return this.identifiers[queryName];
    }

    public async requestQuery(querySourceId: string, request: IQueryPageRequest): Promise<QueryData> {
        const key: string = await this.getKey(querySourceId, request);

        if (!key) return null;

        this.queries[key] ??= await this.resolver.getQuery(querySourceId, request);

        return this.queries[key];
    }

    public async requestQueryByName(queryName: string, request: IQueryPageRequest): Promise<QueryData> {
        const querySourceId: string = await this.requestQueryId(queryName);

        if (!querySourceId) return null;

        return this.requestQuery(querySourceId, request);
    }

    private async getKey(querySourceId: string, request: IQueryPageRequest): Promise<string> {
        if (!querySourceId || !request) return null;

        const normalized: IQueryPageRequest = this.normalizeRequest(request);

        const hash: string = await this.stableHash(normalized);

        if (!hash) return null;

        return `${querySourceId}:${hash}`;
    }

    private normalizeRequest(request: IQueryPageRequest): IQueryPageRequest {
        if (!request) return null;

        // Normalize grid settings (fields in the same order, default values for null).
        const gridSettings: IPagedGridSettings = {
            skip: request.gridSettings?.skip ?? 0,
            take: request.gridSettings?.take ?? 0,
            sortingField: request.gridSettings?.sortingField,
            sortingDirection: request.gridSettings?.sortingDirection ?? 0,
        };

        // Normalize variables (fields in the same order, alphabetic order for variables).
        const queryVariables: IQueryVariables = {
            variables: (request.queryVariables?.variables ?? [])
                .map(variable => <IEmittedVariable>{
                    name: variable.name,
                    value: variable.value,
                    empty: variable.empty,
                    $type: variable.$type,
                    behaviorOnEmpty: variable.behaviorOnEmpty,
                })
                .sort((first, second) => first.name.localeCompare(second.name))
        };

        return { gridSettings, queryVariables };
    }

    private async stableHash(obj: any): Promise<string> {
        if (!obj) return null;

        const json: string = JSON.stringify(obj);
        const data: BufferSource = new TextEncoder().encode(json);

        const digest: ArrayBuffer = await crypto.subtle.digest("SHA-256", data);

        const bytes: number[] = Array.from(new Uint8Array(digest));
        const hex: string[] = bytes.map(byte => byte.toString(16).padStart(2, "0"));

        return hex.join("");
    }
}
